import type { UseDataInfo } from "QueryTypes";
import type { SimulatorState } from "Models";
import { useRequest } from "redux-query-react";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { requestAsync } from "redux-query";
import { getSimulatorStateQuery } from "./simulatorStateQueries";
import { useSelector } from "react-redux";
import type { EntitiesById, RootState } from "StoreTypes";
export const useSimulatorState = (
  simId: string,
): UseDataInfo<SimulatorState | undefined> => {
  const [queryState, refreshSimulatorState] = useRequest(
    getSimulatorStateQuery(simId),
  );

  const simulatorState = useSelector<RootState, SimulatorState | undefined>(
    (state: RootState) => state.entities.simulatorStates?.[simId],
  );

  return [simulatorState, queryState, refreshSimulatorState];
};

/**
 * Every simulator state in the normalised store, keyed by simulator id.
 *
 * For callers that need several bays' statuses at once — the per-sim hook
 * can't be called in a loop — and so must look them up themselves. A sim with
 * no entry here has reported nothing, which is a meaningful answer rather than
 * a missing one.
 */
export const useAllSimulatorStates = (): EntitiesById<SimulatorState> =>
  useSelector(
    (state: RootState) => state.entities.simulatorStates ?? EMPTY_STATES,
  );

/** Stable identity, so the selector above doesn't re-render on every store tick. */
const EMPTY_STATES: EntitiesById<SimulatorState> = {};

/**
 * Read one simulator's state from the API, now, and hand it back.
 *
 * The store's copy is kept live by the status websocket, which only carries
 * the facility the operator is signed in to — so for any other facility it can
 * be minutes old, or absent. Callers about to act on a bay's status (sending a
 * round to it, say) ask for it directly rather than trusting the cache.
 *
 * The state also lands in the store, so anything rendering from the cache
 * catches up for free.
 */
export const useReadSimulatorState = (): ((
  simId: string,
) => Promise<SimulatorState | undefined>) => {
  const dispatch = useDispatch();
  return useCallback(
    async (simId: string) => {
      // The query normalises into `{ simulatorStates: { [id]: … } }`, which
      // redux-query hands back as `transformed` and, once merged, `entities`.
      // Either is the same shape; whichever is present is read.
      // redux-query's middleware turns this dispatch into a promise; the
      // store's own dispatch type doesn't know that, hence the cast.
      //
      // Forced: callers use this precisely because the cached copy might be
      // stale, so resolving from cache would answer the wrong question.
      const pending = dispatch(
        requestAsync(getSimulatorStateQuery(simId, { force: true })),
      ) as unknown as Promise<NormalisedStateResult | undefined>;
      const result = await pending;
      const entities = result?.entities ?? result?.transformed;
      return entities?.simulatorStates?.[simId];
    },
    [dispatch],
  );
};

/** What a normalised simulator-state request resolves with. */
interface NormalisedStateResult {
  entities?: { simulatorStates?: EntitiesById<SimulatorState> };
  transformed?: { simulatorStates?: EntitiesById<SimulatorState> };
}
